import { useState, useEffect, useCallback, useRef } from 'react';

interface UseNetworkStatusReturn {
  isOnline: boolean;
  registerRetryCallback: (callback: () => void) => void;
  unregisterRetryCallback: (callback: () => void) => void;
}

/**
 * Hook to monitor network connection status and manage retry callbacks
 * Uses browser's navigator.onLine API for network detection
 * Designed to work alongside GraphQL error detection for comprehensive network error handling
 */
export const useNetworkStatus = (): UseNetworkStatusReturn => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  
  const retryCallbacksRef = useRef<Set<() => void>>(new Set());
  const previousOnlineStateRef = useRef<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  // Register a callback to be called when connection is restored
  const registerRetryCallback = useCallback((callback: () => void) => {
    retryCallbacksRef.current.add(callback);
  }, []);

  // Unregister a callback
  const unregisterRetryCallback = useCallback((callback: () => void) => {
    retryCallbacksRef.current.delete(callback);
  }, []);

  // Execute all registered retry callbacks
  const executeRetryCallbacks = useCallback(() => {
    retryCallbacksRef.current.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        // Silently handle errors in retry callbacks
        // Components should handle their own error states
      }
    });
  }, []);

  useEffect(() => {
    const updateOnlineStatus = () => {
      const wasOffline = !previousOnlineStateRef.current;
      const isNowOnline = navigator.onLine;

      setIsOnline(isNowOnline);

      // If connection was restored, execute retry callbacks
      if (wasOffline && isNowOnline) {
        executeRetryCallbacks();
      }

      previousOnlineStateRef.current = isNowOnline;
    };

    // Add event listeners for network status changes
    globalThis.addEventListener('online', updateOnlineStatus);
    globalThis.addEventListener('offline', updateOnlineStatus);

    // Cleanup
    return () => {
      globalThis.removeEventListener('online', updateOnlineStatus);
      globalThis.removeEventListener('offline', updateOnlineStatus);
    };
  }, [executeRetryCallbacks]);

  return {
    isOnline,
    registerRetryCallback,
    unregisterRetryCallback,
  };
};
